import { useEffect, useRef, useState, type ReactNode } from 'react';
import { allocateSheetId, useSheetStackStore } from '../../store/sheet-stack-store.js';

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Optional content rendered at the end of the sticky header row. */
  headerEnd?: ReactNode;
}

/**
 * Full-screen slide-up sheet for sub-`lg` viewports (android-app-plan M2.4):
 * sticky safe-area-padded header with a close button, scroll-locked body,
 * Escape-to-close, and registration on the sheet stack so the Android back
 * button dismisses the top-most sheet first.
 *
 * Used by the mobile inbox (M2.3) and, from M3, the task popup / edit
 * drawer / AI panel / filter sheets.
 */
export function Sheet({ open, onClose, title, children, headerEnd }: SheetProps) {
  const idRef = useRef<number | null>(null);
  if (idRef.current === null) idRef.current = allocateSheetId();
  const sheetId = idRef.current;

  // Two-frame mount so the translate transition actually animates in.
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (!open) {
      setEntered(false);
      return;
    }
    const raf = requestAnimationFrame(() => setEntered(true));

    // Scroll-lock the page behind the sheet.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Escape dismisses, matching the desktop drawer conventions.
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);

    // Register on the sheet stack for back-button handling.
    useSheetStackStore.getState().push({ id: sheetId, close: onClose });

    return () => {
      cancelAnimationFrame(raf);
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
      useSheetStackStore.getState().remove(sheetId);
    };
  }, [open, onClose, sheetId]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex flex-col bg-white"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className={`flex h-full flex-col transition-transform duration-200 ease-out ${
          entered ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <header className="sticky top-0 z-10 flex items-center gap-2 border-b border-slate-200 bg-white px-4 py-3 pt-[calc(0.75rem+env(safe-area-inset-top))]">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-ml-2 inline-flex h-10 w-10 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-bn-300"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-5 w-5"
              aria-hidden="true"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <h2 className="min-w-0 flex-1 truncate text-base font-semibold text-slate-900">
            {title}
          </h2>
          {headerEnd}
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto pb-[env(safe-area-inset-bottom)]">
          {children}
        </div>
      </div>
    </div>
  );
}
